// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import { useCallback, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ThemeContext } from 'styled-components'
import {
  Box,
  NameValueList,
  NameValuePair,
  Page,
  PageContent,
  PageHeader
} from 'grommet'
import dayjs from 'dayjs'
import { FormDown, Previous } from 'grommet-icons'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Layout } from '../../../../commoncomponents/layout/Layout'
import {
  ActionButton,
  Anchor,
  Loader,
  ObjectList,
  Typography
} from '../../../../components'
import NoViewPermission from '../../commoncomponents/NoViewPermission'
import { get } from '../../../../utils/api-utils'
import { displayApiError } from '../../../../utils/error-handling-utils'
import { renameKeys } from '../../../../utils/common-utils'
import { displayNotification } from '../../../../utils/notificiation-utils'
import {
  getAuthorizationText,
  idp,
  IdpConfig,
  ssoModeEnum,
  getAuthorizationTextLD,
  generateRedirectUrl
} from '../constants'
import { useVisibilityContext } from '../../../../context/visibility-context'
import Markdown from '../../../../commoncomponents/Markdown'
import useOrganizationAssociation from '../../commoncomponents/useOrganizationAssociation'
import { isMSP, isV2 } from '../../../../utils/feature-flag-utils'

import { DeleteSsoProfileModal } from './DeleteSsoProfileModal'

const ViewSsoProfileContent = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'iam'])
  const viewLocation = useLocation()
  // Allow HPE theme styling to spread onto Typography instances
  // for names in a name value pair
  const {
    nameValuePair: { name }
  } = useContext(ThemeContext)

  const [loading, setLoading] = useState(false)
  const [ssoProfileName, setSsoProfileName] = useState('')
  const [samlConfig, setSamlConfig] = useState({})
  const [oidcConfig, setOidcConfig] = useState({})
  const [samlAuthentication, setsamlAuthentication] = useState('')
  const [generalLD, setGeneralLD] = useState({})
  const [samlAttributeMapping, setSamlAttributeMapping] = useState({})
  const [ssoIdp, setSsoIdp] = useState({})
  const [sessionTimeout, setSessionTimeout] = useState({})
  const [oidcSesstionTimeout, setOidcSesionTimeout] = useState({})
  const [recoveryAccount, setRecoveryAccount] = useState({})
  const [apiErrorNotification, setApiErrorNotification] = useState(null)
  const [certificate, setCertificate] = useState('')
  const [showCertificate, setShowCertificate] = useState(false)
  const LDFlags = useFlags()
  const isGlcpOidc = LDFlags['glcp-oidc']
  const [ssoRoutingRule, setSsoRoutingRule] = useState(null)

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Notifications
  const [notifTitle, setNotifTitle] = useState('')
  const [notifMsg, setNotifMsg] = useState('')
  const [notifSeverity, setNotifSeverity] = useState('')
  const setNotificationInfo = (message, severity, title = '') => {
    setNotifTitle(title)
    setNotifMsg(message)
    setNotifSeverity(severity)
  }

  // Permissions
  const { v2RbacPolicies, v2RbacRootWkspcPolicies } = useVisibilityContext()
  const v2SsoProfilesReadPerm =
    v2RbacPolicies?.includes('identity.sso-profiles.read') &&
    v2RbacPolicies?.includes('identity.sso-routing-rules.read')
  const v2SsoProfilesUpdatePerm =
    v2RbacPolicies?.includes('identity.sso-profiles.update') &&
    v2RbacPolicies?.includes('identity.sso-routing-rules.update')
  const v2SsoProfilesDeletePerm =
    v2RbacPolicies?.includes('identity.sso-profiles.delete') &&
    v2RbacPolicies?.includes('identity.sso-routing-rules.delete')
  const v2AssociateSsoProfilesReadPerm =
    v2RbacRootWkspcPolicies?.includes('identity.sso-profiles.read') &&
    v2RbacRootWkspcPolicies?.includes('identity.sso-routing-rules.read')
  const v2AssociatedSsoProfilesUpdatePerm =
    v2RbacRootWkspcPolicies?.includes('identity.sso-profiles.update') &&
    v2RbacRootWkspcPolicies?.includes('identity.sso-routing-rules.update')
  const v2AssociatedSsoProfilesDeletePerm =
    v2RbacRootWkspcPolicies?.includes('identity.sso-profiles.delete') &&
    v2RbacRootWkspcPolicies?.includes('identity.sso-routing-rules.delete')
  const { isAssociated, isAssociateLoader } =
    useOrganizationAssociation(setNotificationInfo)

  const providerConfiguration = {
    [t('iam:sso_profiles.view_edit_create.greenlake_entity_id')]:
      idp.entityIdUrl,
    [t('iam:sso_profiles.view_edit_create.acs_url')]: idp.acsUrl,
    [t('iam:sso_profiles.view_edit_create.default_relay_state')]:
      idp.defaultRelayStateUrl
  }

  const tempI18NKeys = {
    samlConfig: {
      domains: t('iam:sso_profiles.view_edit_create.domain'),
      ssoMode: t('iam:sso_profiles.view_edit_create.authorization'),
      ...(isMSP() &&
        isV2() && {
          applies: t('iam:sso_profiles.view_edit_create.applies_to')
        }),
      created_at: t('iam:sso_profiles.view_edit_create.created_at'),
      updated_at: t('iam:sso_profiles.view_edit_create.updated_at')
    },
    generalLD: {
      domains: t('iam:sso_profiles.view_edit_create.domain'),
      authentication: t('iam:sso_profiles.view_edit_create.authentication'),
      ssoMode: t('iam:sso_profiles.view_edit_create.authorization'),
      ...(isMSP() && {
        applies: t('iam:sso_profiles.view_edit_create.applies_to')
      }),
      created_at: t('iam:sso_profiles.view_edit_create.created_at'),
      updated_at: t('iam:sso_profiles.view_edit_create.updated_at')
    },
    attrMapping: {
      email: t('iam:sso_profiles.view_edit_create.email_address'),
      firstName: t('iam:sso_profiles.view_edit_create.first_name'),
      lastName: t('iam:sso_profiles.view_edit_create.last_name'),
      ccsAttribute: t('iam:sso_profiles.view_edit_create.ccs_attribute')
    },
    samlIdpConfig: {
      entityId: t('iam:sso_profiles.view_edit_create.idp_entity_id'),
      loginUrl: t('iam:sso_profiles.view_edit_create.login_url'),
      logoutUrl: t('iam:sso_profiles.view_edit_create.logout_url')
    },
    recoveryUser: {
      username: t('iam:sso_profiles.view_edit_create.recovery_username'),
      recoveryEmail: t('iam:sso_profiles.view_edit_create.recovery_email')
    },
    sessionTimeout: {
      sessionTimeout: t('iam:sso_profiles.view_edit_create.session_timeout')
    },
    oidcSessionTimeout: {
      oidcsessionTimeout: t('iam:sso_profiles.view_edit_create.session_timeout')
    },
    openidConnectConfiguration: {
      clientId: t('iam:sso_profiles_oidc.client_label'),
      usePkce: t('iam:sso_profiles_oidc.use_pkce_label'),
      issuer: t('iam:sso_profiles_oidc.issuer_url_label'),
      authorizationEndpoint: t(
        'iam:sso_profiles_oidc.authorization_endpoint_label'
      ),
      tokenEndpoint: t('iam:sso_profiles_oidc.token_endpoint_label'),
      jwksEndpoint: t('iam:sso_profiles_oidc.jwks_endpoint_label'),
      userInfoEndpoint: t('iam:sso_profiles_oidc.userinfo_endpoint'),
      greenlakeRedirectUrl: t(
        'iam:sso_profiles_oidc.greenlake_redirect_url_label'
      )
    }
  }

  const getValueFromArrayObj = (key, arr = []) => {
    return arr.find((obj) => obj.name === key)?.expression
  }

  const initialize = () => {
    // Initialize fields so we display "--" in the screen as value instead of to ignore it
    const attributeMappingConfig = renameKeys(tempI18NKeys.attrMapping, {
      email: '',
      // country: '',
      firstName: '',
      lastName: '',
      ccsAttribute: ''
    })
    setSamlAttributeMapping(attributeMappingConfig)

    const updatedSamlConfig = renameKeys(tempI18NKeys.samlConfig, {
      domains: '',
      ssoMode: '',
      ...(isMSP() && isV2() && { applies: '' }),
      created_at: null,
      updated_at: null
    })
    setSamlConfig(updatedSamlConfig)

    const updatedGeneralLD = renameKeys(tempI18NKeys.generalLD, {
      domains: '',
      authentication: '',
      ssoMode: '',
      applies: '',
      created_at: null,
      updated_at: null
    })
    setGeneralLD(updatedGeneralLD)

    const timeoutConfig = renameKeys(tempI18NKeys.sessionTimeout, {
      sessionTimeout: null
    })
    setSessionTimeout(timeoutConfig)

    const oidcTimeoutConfig = renameKeys(tempI18NKeys.oidcSessionTimeout, {
      oidcSesstionTimeout: null
    })
    setOidcSesionTimeout(oidcTimeoutConfig)

    const recoveryUserConfig = renameKeys(tempI18NKeys.recoveryUser, {
      username: '',
      recoveryEmail: ''
    })
    setRecoveryAccount(recoveryUserConfig)

    const openIdConfig = renameKeys(tempI18NKeys.openidConnectConfiguration, {
      client: '',
      usePkce: '',
      issuer: '',
      authorizationEndpoint: '',
      tokenEndpoint: '',
      jwksEndpoint: '',
      userInfoEndpoint: '',
      greenlakeRedirectUrl: ''
    })
    setOidcConfig(openIdConfig)
  }
  const getPkceValue = (value) => {
    let text = ''
    switch (true) {
      case value === true:
        text = 'Yes'
        return text
      case value === false:
        text = 'No'
        return text
      default:
        break
    }
    return text
  }
  const fetchSsoProfile = useCallback(() => {
    initialize()
    setLoading(true)

    get(`/identity/v1alpha1/sso-profiles/${id}`, {}).then(
      (ssoResponse) => {
        setSsoProfileName(ssoResponse?.data.name)
        setsamlAuthentication(ssoResponse?.data)
        const idpConfig = renameKeys(
          tempI18NKeys.samlIdpConfig,
          ssoResponse?.data?.samlIdpConfig
        )
        setCertificate(ssoResponse?.data?.samlIdpConfig?.certificate)
        setShowCertificate(false)
        setSsoIdp(idpConfig)

        if (ssoResponse?.data?.recoveryUser) {
          const recoveryConfig = renameKeys(
            tempI18NKeys.recoveryUser,
            ssoResponse?.data?.recoveryUser
          )
          setRecoveryAccount(recoveryConfig)
        }

        if (ssoResponse?.data?.attributeMapping?.length) {
          const attributeMapping = ssoResponse?.data?.attributeMapping
          const attributeMappingConfig = renameKeys(tempI18NKeys.attrMapping, {
            email: getValueFromArrayObj('Email', attributeMapping),
            firstName: getValueFromArrayObj('FirstName', attributeMapping),
            lastName: getValueFromArrayObj('LastName', attributeMapping),
            ccsAttribute: getValueFromArrayObj(
              'HPECCSAttribute',
              attributeMapping
            )
          })
          setSamlAttributeMapping(attributeMappingConfig)

          const timeoutConfig = renameKeys(tempI18NKeys.sessionTimeout, {
            sessionTimeout: getValueFromArrayObj(
              'IdleSessionTimeout',
              attributeMapping
            )
          })
          setSessionTimeout(timeoutConfig)
        }
        // IF we get oidcIdpConfig from api
        if (ssoResponse?.data?.oidcIdpConfig) {
          const oidc_config = renameKeys(
            tempI18NKeys.openidConnectConfiguration,
            {
              ...ssoResponse?.data?.oidcIdpConfig,
              usePkce: getPkceValue(ssoResponse.data.oidcIdpConfig.usePkce),
              greenlakeRedirectUrl: generateRedirectUrl(
                ssoResponse.data.oidcIdpConfig.issuer
              )
            }
          )
          setOidcConfig(oidc_config)
          // sessiontimeout of oidcIdpConfig
          const oidcTimeoutConfig = renameKeys(
            tempI18NKeys.oidcSessionTimeout,
            {
              oidcsessionTimeout: ssoResponse.data.oidcIdpConfig.sessionTimeout
            }
          )
          setOidcSesionTimeout(oidcTimeoutConfig)
        }
        setLoading(false)
      },
      (error) => {
        setLoading(false)
        setApiErrorNotification(
          displayApiError(error, t, setApiErrorNotification)
        )
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  const getAppliesTo = (value) => {
    let appliesToText = ''
    switch (value) {
      case ssoModeEnum.AUTHENTICATION_ONLY:
      case ssoModeEnum.AUTHORIZATION:
        appliesToText = t('iam:sso_profiles.view_edit_create.msp_organization')
        return appliesToText
      case ssoModeEnum.AUTHORIZATION_MSP_MANAGED_TENANT_ONLY:
      case ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED_TENANT_ONLY:
        appliesToText = t(
          'iam:sso_profiles.view_edit_create.customer_organizations'
        )
        return appliesToText
      case ssoModeEnum.AUTHORIZATION_MSP_MANAGED:
      case ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED:
        appliesToText = t(
          'iam:sso_profiles.view_edit_create.msp_and_customer_organizations'
        )
        return appliesToText
      default:
        break
    }
    return appliesToText
  }

  const fetchSsoRouting = useCallback(() => {
    setLoading(true)
    get(`/internal-identity/v1alpha1/sso-routing-rules`, {
      'sso-profile-id': id
    }).then(
      (res) => {
        const updatedSamlConfig = renameKeys(tempI18NKeys.samlConfig, {
          ...res?.data?.items[0],
          domains: res?.data?.items[0]?.conditions?.domains?.join(','),
          ...(isV2()
            ? {
                ssoMode: getAuthorizationTextLD(
                  res?.data?.items[0]?.actions?.idp?.ssoMode
                )
              }
            : {
                ssoMode: getAuthorizationText(
                  res?.data?.items[0]?.actions?.idp?.ssoMode
                )
              }),
          ...(isMSP() &&
            isV2() && {
              applies: getAppliesTo(res?.data?.items[0]?.actions?.idp?.ssoMode)
            }),
          created_at: dayjs(res?.data?.items[0]?.updatedAt).format(
            'MM/DD/YYYY'
          ),
          updated_at: dayjs(res?.data?.items[0]?.createdAt).format('MM/DD/YYYY')
        })
        setSamlConfig(updatedSamlConfig)
        setSsoRoutingRule(res?.data?.items[0])
        setLoading(false)
      },
      (error) => {
        setLoading(false)
        setApiErrorNotification(
          displayApiError(error, t, setApiErrorNotification)
        )
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    const { notification, notificationError } = viewLocation?.state || {}

    const notifyMessage = notification ? (
      <Markdown>{notification}</Markdown>
    ) : null
    const notifyErrorMessage = notificationError ? (
      <Markdown>{notificationError}</Markdown>
    ) : null

    if (notifyMessage) {
      setNotificationInfo(notifyMessage, 'info')
    } else if (notifyErrorMessage) {
      setNotificationInfo(notifyErrorMessage, 'error')
    }

    viewLocation.state = {
      ...viewLocation.state,
      notification: null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewLocation?.state?.notification,
    viewLocation?.state?.notificationError
  ])

  useEffect(() => {
    fetchSsoRouting()
  }, [fetchSsoRouting])

  useEffect(() => {
    fetchSsoProfile()
  }, [fetchSsoProfile])

  // For the aignment of values comming from ssoprofile and routing-rules api
  useEffect(() => {
    const getAuthenticationText = (valuess) => {
      let text = ''
      switch (true) {
        case IdpConfig.oidcConfig in valuess:
          text = 'OIDC'
          return text
        case IdpConfig.samlConfig in valuess:
          text = 'SAML 2.0'
          return text
        default:
          break
      }
      return text
    }
    const getAuthorization = (value) => {
      let text = ''
      switch (value.ssoMode) {
        case ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED:
        case ssoModeEnum.AUTHENTICATION_ONLY:
        case ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED_TENANT_ONLY:
          text = t('iam:sso_profiles.view_edit_create.local_role_assignments')
          return text
        case ssoModeEnum.AUTHORIZATION_MSP_MANAGED:
        case ssoModeEnum.AUTHORIZATION:
        case ssoModeEnum.AUTHORIZATION_MSP_MANAGED_TENANT_ONLY:
          text = t('iam:sso_profiles.view_edit_create.sso_role_assignments')
          return text
        default:
          break
      }
      return text
    }
    // align once the data collected from ssoprofiles and routingrules api
    if (ssoRoutingRule && samlAuthentication) {
      const updated_GeneralLD = renameKeys(tempI18NKeys.generalLD, {
        domains: ssoRoutingRule.conditions?.domains?.join(','),
        authentication: getAuthenticationText(samlAuthentication),
        ssoMode: getAuthorization(ssoRoutingRule.actions?.idp),
        ...(isMSP() && {
          applies: getAppliesTo(ssoRoutingRule.actions?.idp?.ssoMode)
        }),
        created_at: dayjs(ssoRoutingRule.updatedAt).format('MM/DD/YYYY'),
        updated_at: dayjs(ssoRoutingRule.createdAt).format('MM/DD/YYYY')
      })
      setGeneralLD(updated_GeneralLD)
    }

    if (samlAuthentication && !ssoRoutingRule) {
      const updated_GeneralLD = renameKeys(tempI18NKeys.generalLD, {
        domains: '--',
        authentication: getAuthenticationText(samlAuthentication),
        ssoMode: '--',
        created_at: '--',
        updated_at: '--'
      })
      setGeneralLD(updated_GeneralLD)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoRoutingRule, samlAuthentication])

  const handleBackBtnClick = () => {
    navigate(`/manage-account/organization/sso-profiles`)
  }

  const ssoProfileDeleteSuccess = () => {
    navigate(`/manage-account/organization/sso-profiles`, {
      state: {
        notification: t('iam:domains.sso_delete_notification', {
          ssoProfileName
        })
      }
    })
  }

  const handleEdit = () => {
    navigate(`/manage-account/organization/sso-profiles/edit-sso-profile/${id}`)
  }

  const tableActions = () => {
    const actions = []
    const edit = {
      label: t('common:edit'),
      testId: 'edit-sso-profile-btn',
      onClick: handleEdit,
      visibility: {
        rbac: {
          permission: 'identity.sso-profiles.update'
        }
      }
    }
    const del = {
      label: t('common:delete'),
      onClick: () => {
        setShowDeleteDialog(true)
      },
      visibility: {
        rbac: {
          permission: 'identity.sso-profiles.delete'
        }
      },
      testId: 'remove-sso-profile-btn'
    }

    if (
      (!isAssociated && v2SsoProfilesUpdatePerm && v2SsoProfilesReadPerm) ||
      (isAssociated &&
        v2AssociatedSsoProfilesUpdatePerm &&
        v2AssociateSsoProfilesReadPerm)
    )
      actions?.push(edit)
    if (
      (!isAssociated && v2SsoProfilesDeletePerm && v2SsoProfilesReadPerm) ||
      (isAssociated &&
        v2AssociatedSsoProfilesDeletePerm &&
        v2AssociateSsoProfilesReadPerm)
    )
      actions?.push(del)
    return actions
  }

  const ssoProfileTitle = () => {
    return v2SsoProfilesReadPerm ? ssoProfileName : ''
  }

  const samlProfileData = () => {
    return (
      <Box gap="small">
        {samlAttributeMapping && (
          <ObjectList
            data={samlAttributeMapping}
            showRuler={false}
            title={t(
              'iam:sso_profiles.view_edit_create.saml_attribute_mappings'
            )}
            testId="samlAttributeMapping-data-list"
          />
        )}
        {providerConfiguration && (
          <ObjectList
            data={providerConfiguration}
            showRuler={false}
            testId="providerConfiguration-data-list"
            title={t(
              'iam:sso_profiles.view_edit_create.identity_provider_configuration'
            )}
          />
        )}
        {ssoIdp && (
          <>
            <ObjectList
              data={ssoIdp}
              showRuler={false}
              testId="ssoIdp-data-list"
              title={t(
                'iam:sso_profiles.view_edit_create.greenlake_configuration'
              )}
            />
            <Box direction="row" pad={{ vertical: 'small' }}>
              <NameValueList
                valueProps={{ width: ['auto', 'medium'] }}
                data-testid="folder-details"
              >
                <NameValuePair
                  key="certificate"
                  name={
                    <Typography
                      type="text"
                      testId="certificate-label"
                      {...name}
                    >
                      {t('iam:sso_profiles.view_edit_create.certificate')}
                    </Typography>
                  }
                >
                  {showCertificate ? (
                    <Box direction="column" gap="xxsmall">
                      <Anchor
                        data-testid="devices-anchor-btn"
                        label={t('iam:sso_profiles.view_edit_create.hide')}
                        onClick={() => {
                          setShowCertificate(!showCertificate)
                        }}
                      />
                      <Typography
                        size="medium"
                        color="begin-cert"
                        testId="details-value"
                        type="text"
                      >
                        {`-----${t(
                          'iam:sso_profiles.view_edit_create.begin_cert'
                        )}-----`}
                      </Typography>
                      <Typography
                        size="medium"
                        color="text-strong"
                        testId="details-value"
                        wordBreak="break-all"
                        type="text"
                      >
                        {certificate ?? '--'}
                      </Typography>
                      <Typography
                        size="medium"
                        color="text-strong"
                        testId="end-cert"
                        type="text"
                      >
                        {`-----${t(
                          'iam:sso_profiles.view_edit_create.end_certificate'
                        )}-----`}
                      </Typography>
                    </Box>
                  ) : (
                    <Anchor
                      data-testid="devices-anchor-btn"
                      label={t('iam:sso_profiles.view_edit_create.show')}
                      onClick={() => {
                        setShowCertificate(!showCertificate)
                      }}
                    />
                  )}
                </NameValuePair>
              </NameValueList>
            </Box>
          </>
        )}
      </Box>
    )
  }

  return (
    <Box
      pad={{ horizontal: 'xlarge', bottom: 'large' }}
      width="xxlarge"
      alignSelf="center"
    >
      <Page>
        <PageContent>
          {loading ||
          isAssociateLoader ||
          (!isAssociated && v2SsoProfilesReadPerm === undefined) ||
          (isAssociated && v2AssociateSsoProfilesReadPerm === undefined) ? (
            <Box direction="row" align="center" justify="center" pad="medium">
              <Loader testId="saml-view-loader" />
            </Box>
          ) : (
            <>
              <PageHeader
                responsive
                parent={
                  <Anchor
                    icon={<Previous />}
                    href="#"
                    label={t('iam:sso_profiles.title')}
                    onClick={(event) => {
                      event.preventDefault()
                      handleBackBtnClick()
                    }}
                    data-testid="back-btn"
                  />
                }
                title={
                  <Typography
                    level="1"
                    type="heading"
                    testId="sso-profile-details-title"
                  >
                    {ssoProfileTitle()}
                  </Typography>
                }
                actions={
                  <Box direction="row" align="center" gap="medium">
                    {tableActions()?.length > 0 && (
                      <ActionButton
                        label={t('common:actions')}
                        reverse
                        actions={tableActions()}
                        icon={<FormDown />}
                        testId="sso-profile-details-action-btn"
                        showOneActionAsDropDown
                      />
                    )}
                  </Box>
                }
                data-testid="heading-page-title"
              />

              {(!isAssociated && v2SsoProfilesReadPerm === true) ||
              (isAssociated && v2AssociateSsoProfilesReadPerm === true) ? (
                <Box gap="small" width="large">
                  {isGlcpOidc ? (
                    <Box gap="small">
                      {generalLD && (
                        <ObjectList
                          data={generalLD}
                          showRuler={false}
                          title={t('iam:sso_profiles.view_edit_create.general')}
                          testId="samlConfig-data-list"
                        />
                      )}
                      {samlAuthentication?.oidcIdpConfig && ( // OIDC profile
                        <ObjectList
                          data={oidcConfig}
                          showRuler={false}
                          title="OpenID connect configuration"
                          testId="OpenIdConfig-data-list"
                        />
                      )}
                      {samlAuthentication?.samlIdpConfig && // SAML profile
                        samlProfileData()}
                    </Box>
                  ) : (
                    <Box gap="small">
                      {samlConfig && (
                        <ObjectList
                          data={samlConfig}
                          showRuler={false}
                          title={t('iam:sso_profiles.view_edit_create.general')}
                          testId="samlConfig-data-list"
                        />
                      )}
                      {samlProfileData()}
                    </Box>
                  )}
                  {recoveryAccount && (
                    <ObjectList
                      data={recoveryAccount}
                      showRuler={false}
                      testId="recoveryAccount-data-list"
                      title={t(
                        'iam:sso_profiles.view_edit_create.recovery_user'
                      )}
                    />
                  )}
                  {sessionTimeout && (
                    <ObjectList
                      data={
                        isGlcpOidc && samlAuthentication?.oidcIdpConfig
                          ? oidcSesstionTimeout
                          : sessionTimeout
                      }
                      showRuler={false}
                      testId="sessionTimeout-data-list"
                      title={t(
                        'iam:sso_profiles.view_edit_create.session_timeout'
                      )}
                    />
                  )}
                </Box>
              ) : (
                <NoViewPermission />
              )}
            </>
          )}
          {apiErrorNotification}
          {showDeleteDialog && (
            <DeleteSsoProfileModal
              id={id}
              ssoRoutingRule={ssoRoutingRule}
              setShowModal={() => setShowDeleteDialog(false)}
              onSuccess={ssoProfileDeleteSuccess}
              recoveryAccount={
                recoveryAccount[tempI18NKeys.recoveryUser.username]
              }
            />
          )}
        </PageContent>
      </Page>
      {notifMsg &&
        displayNotification(notifMsg, notifSeverity, setNotifMsg, notifTitle)}
    </Box>
  )
}

const ViewSsoProfile = () => {
  return (
    <Layout>
      <ViewSsoProfileContent />
    </Layout>
  )
}
export default ViewSsoProfile
